import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { CheckoutElementsProvider, PaymentElement, useCheckout } from '@stripe/react-stripe-js/checkout';
import { Lock } from 'lucide-react';
import { useFunnelConfig } from './ThreeJobs';
import { PageCtx, useFunnelContent, pageBg } from './editor/EditableBits';
import { FunnelSections } from './editor/registry';
import { PageWidgets } from './editor/Widgets';
import '../../components/landing/arc/arc.css';

const API = process.env.REACT_APP_BACKEND_URL;

const stripePromiseCache = {};
const getStripe = (pk) => {
  if (!stripePromiseCache[pk]) stripePromiseCache[pk] = loadStripe(pk);
  return stripePromiseCache[pk];
};

// Payment Element styled to match the funnel's bone/ink theme
const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#141414',
    colorBackground: '#ffffff',
    colorText: '#141414',
    colorTextSecondary: 'rgba(20,20,20,0.6)',
    colorTextPlaceholder: 'rgba(20,20,20,0.35)',
    colorDanger: '#b91c1c',
    fontFamily: 'Manrope, system-ui, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '14px',
    focusBoxShadow: '0 0 0 2px rgba(20,20,20,0.25)',
  },
};
const STRIPE_FONTS = [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap' }];

// Inner pay form — lives inside CheckoutElementsProvider
const KitPayForm = ({ total }) => {
  const state = useCheckout();
  const [paying, setPaying] = useState(false);

  if (state.type === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="kit-checkout-loading">
        <div className="w-8 h-8 border-2 border-[#141414]/15 border-t-[#141414] rounded-full animate-spin mb-3" />
        <p className="arc-body text-sm text-[#141414]/50">Loading secure checkout…</p>
      </div>
    );
  }
  if (state.type === 'error') {
    return <p className="arc-body text-sm text-red-700 p-6 text-center">{state.error?.message || 'Checkout could not load — refresh the page'}</p>;
  }

  const pay = async () => {
    setPaying(true);
    try {
      const res = await state.checkout.confirm();
      if (res && res.type === 'error') {
        toast.error(res.error?.message || 'Payment failed — please check your card details');
        setPaying(false);
      }
      // on success Stripe redirects to the VIP page automatically
    } catch (e) {
      toast.error('Payment failed — please try again');
      setPaying(false);
    }
  };

  return (
    <div className="p-5 sm:p-6" data-testid="kit-pay-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        onClick={pay}
        disabled={paying}
        className="arc-body w-full mt-5 rounded-full bg-[#141414] text-[#F0EFEC] text-lg font-bold px-8 py-4 hover:bg-black transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid="kit-pay-btn"
      >
        {paying ? (
          <>
            <span className="w-4 h-4 border-2 border-[#F0EFEC]/30 border-t-[#F0EFEC] rounded-full animate-spin" />
            Processing…
          </>
        ) : (
          <>
            <Lock size={16} /> Pay ${total} — Get Instant Access
          </>
        )}
      </button>
    </div>
  );
};

export default function ChallengeKitPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const regId = searchParams.get('reg');
  const config = useFunnelConfig();
  const pages = useFunnelContent();
  const [bump, setBump] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null); // {client_secret, publishable_key, session_id}
  const [checkoutLoading, setCheckoutLoading] = useState(true);
  const [hostedUrl, setHostedUrl] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!regId) navigate('/challenge');
  }, [regId, navigate]);

  // Create (or re-create) the checkout session — on load and whenever the bump toggles
  const createSession = useCallback(async (withBump) => {
    setCheckoutLoading(true);
    setCheckoutData(null);
    try {
      // Dedupe StrictMode double-fires: reuse the in-flight request per reg+bump
      const cacheKey = `${regId}:${withBump}`;
      if (!createSession._cache) createSession._cache = {};
      if (!createSession._cache[cacheKey]) {
        createSession._cache[cacheKey] = axios.post(`${API}/api/funnel/checkout`, { reg_id: regId, include_bump: withBump })
          .finally(() => { setTimeout(() => { delete createSession._cache[cacheKey]; }, 2000); });
      }
      const r = await createSession._cache[cacheKey];
      if (r.data.mode === 'custom' || r.data.mode === 'embedded') {
        setHostedUrl('');
        setCheckoutData(r.data);
      } else {
        setHostedUrl(r.data.checkout_url);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed — refresh and try again');
    }
    setCheckoutLoading(false);
  }, [regId]);

  useEffect(() => {
    if (regId) createSession(bump);
  }, [regId, bump, createSession]);

  const decline = async () => {
    setDeclining(true);
    try {
      await axios.post(`${API}/api/funnel/decline`, { reg_id: regId });
    } catch { /* still route through */ }
    navigate('/challenge/thanks');
  };

  const checkoutSlot = (
    <>
      {checkoutLoading && (
        <div className="absolute inset-0 z-10 bg-white flex flex-col items-center justify-center" data-testid="kit-checkout-loading-overlay">
          <div className="w-8 h-8 border-2 border-[#141414]/15 border-t-[#141414] rounded-full animate-spin mb-3" />
          <p className="arc-body text-sm text-[#141414]/50">{bump ? 'Adding the Niche Vault…' : 'Loading secure checkout…'}</p>
        </div>
      )}
      {hostedUrl ? (
        <div className="p-6 text-center">
          <p className="arc-body text-sm text-[#141414]/60 mb-5">You'll complete payment on Stripe's secure page.</p>
          <a href={hostedUrl} className="arc-body inline-block w-full rounded-full bg-[#141414] text-[#F0EFEC] text-lg font-bold px-8 py-4 hover:bg-black transition-colors" data-testid="kit-buy-btn">
            Yes — send me the Launch Kit — ${bump ? 74 : 27} →
          </a>
        </div>
      ) : checkoutData ? (
        <CheckoutElementsProvider
          key={checkoutData.client_secret}
          stripe={getStripe(checkoutData.publishable_key)}
          options={{ clientSecret: checkoutData.client_secret, elementsOptions: { appearance: STRIPE_APPEARANCE, fonts: STRIPE_FONTS, savedPaymentMethod: { enableSave: 'never' } } }}
        >
          <KitPayForm total={bump ? 74 : 27} />
        </CheckoutElementsProvider>
      ) : null}
    </>
  );

  return (
    <PageCtx.Provider value={{ config, handlers: { bump, setBump, checkoutSlot, decline, declining }, jobs: null }}>
      <div className="min-h-screen arc-bone pb-6" style={{ background: pageBg(pages.kit) || undefined }} data-testid="challenge-kit-page">
        <FunnelSections sections={pages.kit} />
        <PageWidgets sections={pages.kit} />

        {config.test_mode && (
          <div className="fixed bottom-4 left-4 z-50 arc-body rounded-full bg-[#141414] text-[#F0EFEC] text-xs font-bold px-4 py-2 shadow-lg" data-testid="funnel-test-mode-chip">
            TEST MODE — card 4242 4242 4242 4242
          </div>
        )}
      </div>
    </PageCtx.Provider>
  );
}
